import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, Lock, Unlock, RefreshCw, AlertCircle, CheckCircle2, FileText, Pencil, Eye, Upload, WandSparkles, Undo2 } from "lucide-react";
import { SpecEditor } from "@/components/proposal/SpecEditor";
import { RewriteSectionDialog } from "@/components/proposal/RewriteSectionDialog";
import ClientLinkPanel from "@/components/ClientLinkPanel";

// Hand-over 2A-2G: the one proposal structure (13 sections). Generate runs through the Claude connector as the
// person who clicked; the person reviews and edits any section here, the tie-out gate runs, and the PDF renders
// from the same spec. No fallback to the old generator.
export default function SpecProposal({ bid, perms = {}, locked: bidSent, onUpload = null }) {
  const [data, setData] = useState(null);
  const [html, setHtml] = useState("");
  const [tieout, setTieout] = useState(null);
  const [mode, setMode] = useState("preview");
  const [busy, setBusy] = useState("");
  const [rewrite, setRewrite] = useState(null);
  const poll = useRef(null);
  const canEdit = !!perms.canPrice && !bidSent;
  const canUnlock = !!perms.canSetMargin;
  const status = data?.status || "none";
  const generating = status === "generating";
  const rewriting = status === "rewriting";
  const isLocked = !!data?.locked;

  const load = async () => {
    try {
      const r = await api.get(`/bids/${bid.bid_id}/proposal/spec`);
      setData(r.data);
      if (r.data.exists) {
        const [h, t] = await Promise.all([api.get(`/bids/${bid.bid_id}/proposal/html`), api.get(`/bids/${bid.bid_id}/proposal/tieout`)]);
        setHtml(h.data); setTieout(t.data);
      }
      return r.data.status;
    } catch { return "none"; }
  };
  useEffect(() => { load(); return () => poll.current && clearInterval(poll.current); }, [bid.bid_id]); // eslint-disable-line

  const watch = () => {
    poll.current && clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const s = await load();
      if (s === "ready") { clearInterval(poll.current); poll.current = null; toast.success("Proposal ready for review"); }
      else if (s === "failed") { clearInterval(poll.current); poll.current = null; toast.error("Generate did not finish. See the message on the tab."); }
    }, 4000);
  };
  useEffect(() => { if ((generating || rewriting) && !poll.current) watch(); }, [generating, rewriting]); // eslint-disable-line

  const startRewrite = async (section, instruction, tone = "keep") => {
    setBusy("rewrite");
    try {
      const r = await api.post(`/bids/${bid.bid_id}/proposal/spec/rewrite`, { section, instruction, tone });
      setRewrite(null); setMode("preview");
      setData((d) => ({ ...(d || {}), status: "rewriting", error: null, meta: { ...(d?.meta || {}), rewriting: section } }));
      toast.info(`Claude is rewriting ${r.data.label}…`);
      watch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the rewrite"); load(); }
    finally { setBusy(""); }
  };
  const askRewrite = async (section, draft) => {
    if (draft) { await save(draft, true); }
    setRewrite({ section });
  };
  const undoRewrite = async () => {
    setBusy("undo");
    try {
      const r = await api.post(`/bids/${bid.bid_id}/proposal/spec/rewrite/undo`);
      await load();
      toast.success(`Brought back the previous wording of ${r.data.undone?.label || "the section"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
    finally { setBusy(""); }
  };

  const generate = async () => {
    try {
      await api.post(`/bids/${bid.bid_id}/proposal/generate`, {});
      setData((d) => ({ ...(d || {}), status: "generating", error: null }));
      toast.info("Claude is building the proposal through the connector…");
      watch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Generate"); load(); }
  };
  const download = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/bids/${bid.bid_id}/proposal/pdf`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "Proposal.pdf";
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded and filed");
    } catch (e) {
      let msg = "Could not render the PDF";
      try { const j = JSON.parse(await e.response?.data?.text()); msg = j.detail?.message || j.detail || msg; } catch { /* keep */ }
      toast.error(msg); load();
    } finally { setBusy(""); }
  };
  const refresh = async () => {
    setBusy("refresh");
    try { await api.post(`/bids/${bid.bid_id}/proposal/spec/refresh`); await load(); toast.success("Figures refreshed from the estimate"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not refresh"); } finally { setBusy(""); }
  };
  const lock = async (v) => {
    try { await api.post(`/bids/${bid.bid_id}/proposal/spec/lock`, { locked: v }); await load(); toast.success(v ? "Proposal locked" : "Proposal unlocked"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change the lock"); }
  };
  const save = async (spec, quiet = false) => {
    setBusy("save");
    try { await api.put(`/bids/${bid.bid_id}/proposal/spec`, { spec }); await load(); setMode("preview"); if (!quiet) toast.success("Proposal saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-500">Loading proposal…</div>;
  const meta = data.meta || {};
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="spec-proposal">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Proposal</h2>
          <p className="text-xs text-slate-500 mt-0.5">One structure, thirteen sections. Generate asks Claude through the connector to write the prose; every figure is the estimate's. Review, edit, then download. The PDF is blocked until every number ties out.</p>
          {meta.generated_at && (
            <p className="text-[11px] text-slate-400 mt-1" data-testid="spec-generated-line">
              Generated {new Date(meta.generated_at).toLocaleString()} by <b>{meta.generated_by}</b>{meta.model ? ` · ${meta.model}` : ""}{meta.edited ? ` · edited by ${meta.edited_by}` : ""}
            </p>
          )}
          {bid.proposal_drive?.web_link && (
            <a href={bid.proposal_drive.web_link} target="_blank" rel="noreferrer" data-testid="proposal-drive-link" className="inline-flex items-center gap-1 mt-1 text-xs text-emerald-700 hover:text-emerald-900">
              <CheckCircle2 className="w-3.5 h-3.5" /> Filed to Google Drive → To Client{bid.proposal_drive.revision_label ? ` (${bid.proposal_drive.revision_label})` : ""}
            </a>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {data.exists && !generating && !rewriting && (
            <>
              <Button variant="outline" onClick={() => setMode(mode === "edit" ? "preview" : "edit")} disabled={mode === "edit" ? false : (!canEdit || isLocked)} data-testid="spec-edit-toggle">
                {mode === "edit" ? <Eye className="w-4 h-4 mr-1.5" /> : <Pencil className="w-4 h-4 mr-1.5" />} {mode === "edit" ? "Preview" : "Edit sections"}
              </Button>
              {canEdit && !isLocked && (
                <Button variant="outline" onClick={() => setRewrite({ section: "intro" })} data-testid="spec-rewrite-section" title="Ask Claude to rewrite one section; everything else stays">
                  <WandSparkles className="w-4 h-4 mr-1.5" /> Rewrite a section
                </Button>
              )}
              <Button variant="outline" onClick={download} disabled={busy === "pdf"} data-testid="spec-download-pdf">
                {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
              </Button>
              {canEdit && !isLocked && (
                <Button variant="outline" onClick={refresh} disabled={busy === "refresh"} data-testid="spec-refresh-figures" title="Re-read every figure from the live estimate; keeps the prose">
                  {busy === "refresh" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Refresh figures
                </Button>
              )}
              {canEdit && (isLocked
                ? (canUnlock && <Button variant="outline" onClick={() => lock(false)} data-testid="spec-unlock"><Unlock className="w-4 h-4 mr-1.5" /> Unlock</Button>)
                : <Button variant="outline" onClick={() => lock(true)} data-testid="spec-lock"><Lock className="w-4 h-4 mr-1.5" /> Lock</Button>)}
            </>
          )}
          {onUpload && !generating && (
            <Button variant="outline" onClick={onUpload} data-testid="proposal-upload-button"><Upload className="w-4 h-4 mr-1.5" /> Upload proposal</Button>
          )}
          {!isLocked && canEdit && !rewriting && (
            <Button onClick={generate} disabled={generating} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="proposal-generate-pdf-button">
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {generating ? "Generating…" : data.exists ? "Regenerate via Claude" : "Generate via Claude"}
            </Button>
          )}
        </div>
      </div>

      <RewriteSectionDialog open={!!rewrite} onOpenChange={(v) => !v && setRewrite(null)} sections={data.rewrite_sections || []}
        initialSection={rewrite?.section} onStart={startRewrite} busy={busy === "rewrite"} />

      {rewriting && (
        <div className="mb-4 rounded-md border border-[#F4C400]/60 bg-[#FFF8D6] text-slate-800 text-xs px-4 py-2.5 flex items-center gap-2" data-testid="spec-rewriting-banner">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Claude is rewriting <b>{(data.rewrite_sections || []).find((s) => s.key === meta.rewriting)?.label || "a section"}</b> through the connector. The rest of the document is untouched; this usually takes under a minute.
        </div>
      )}
      {meta.last_rewrite && !rewriting && (
        <p className="text-[11px] text-slate-400 -mt-2 mb-3 flex items-center gap-2 flex-wrap" data-testid="spec-last-rewrite">
          <span>Last rewrite: {meta.last_rewrite.label} · {new Date(meta.last_rewrite.at).toLocaleString()} by {meta.last_rewrite.by}{meta.last_rewrite.tone && meta.last_rewrite.tone !== "keep" ? ` · ${meta.last_rewrite.tone}` : ""}</span>
          {canEdit && !isLocked && meta.last_rewrite.before !== undefined && (
            <button type="button" onClick={undoRewrite} disabled={busy === "undo"} className="inline-flex items-center gap-1 text-[#003E87] hover:underline disabled:opacity-50" title="Bring back the wording this rewrite replaced" data-testid="spec-undo-rewrite">
              {busy === "undo" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo this rewrite
            </button>
          )}
        </p>
      )}

      {isLocked && (
        <div className="mb-4 rounded-md border border-slate-800 bg-slate-900 text-white text-xs px-4 py-2.5 flex items-center gap-2" data-testid="spec-locked-banner">
          <Lock className="w-3.5 h-3.5" /> Locked{meta.locked_by ? ` by ${meta.locked_by}` : ""}. This is exactly what prints. {canUnlock ? "Unlock to edit or regenerate." : "Ask an owner or chief estimator to unlock."}
        </div>
      )}
      {data.error && !generating && !rewriting && status !== "failed" && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-4 py-2.5 flex items-start gap-2" data-testid="spec-rewrite-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{data.error}</span>
        </div>
      )}
      {status === "failed" && data.error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 text-rose-800 text-xs px-4 py-2.5 flex items-start gap-2" data-testid="spec-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span><b>Generate did not finish.</b> {data.error}</span>
        </div>
      )}
      {generating && (
        <Card className="p-8 flex flex-col items-center text-center gap-3 mb-4" data-testid="proposal-generating">
          <Loader2 className="w-6 h-6 text-[#003E87] animate-spin" />
          <div className="text-sm text-slate-600">Claude is reading the bid, totals, schedule, client and documents through the connector and writing the thirteen sections. This usually takes two to five minutes.</div>
        </Card>
      )}
      {data.exists && !generating && <TieoutPanel tieout={tieout} />}
      {data.exists && !generating && <ClientLinkPanel bid={bid} canManage={!!perms.canSend} />}
      {!data.exists && !generating && (
        <Card className="p-12 flex flex-col items-center text-center gap-3" data-testid="spec-empty">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><FileText className="w-5 h-5 text-slate-400" /></div>
          <div className="text-sm text-slate-500 max-w-xl">Generate the client proposal: the options, scope and quantities (from tagged lines), priced form, responsibility matrix, execution plan, site conditions, weather, billing and cash flow, communication, included, clarifications, excluded and commercial terms. Claude writes the prose through the connector; the figures come from this estimate.</div>
        </Card>
      )}
      {data.exists && !generating && !rewriting && mode === "edit" && <SpecEditor spec={data.spec} onSave={save} onCancel={() => setMode("preview")} saving={busy === "save"} onRewrite={canEdit && !isLocked ? askRewrite : null} />}
      {data.exists && !generating && mode === "preview" && html && (
        <Card className="overflow-hidden" data-testid="spec-preview-card">
          <iframe title="Proposal preview" srcDoc={html} data-testid="spec-preview-frame" className="w-full bg-white" style={{ height: "1400px", border: 0 }} sandbox="" />
        </Card>
      )}
    </div>
  );
}

function Highlight({ text, raw }) {
  if (!text) return null;
  const i = raw ? text.indexOf(raw) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="bg-amber-200 text-amber-950 rounded px-0.5 font-semibold">{raw}</mark>{text.slice(i + raw.length)}</>;
}

function TieoutPanel({ tieout }) {
  const [showAll, setShowAll] = useState(false);
  if (!tieout) return null;
  const issues = [...(tieout.missing || []), ...(tieout.voice || [])];
  const shown = showAll ? issues : issues.slice(0, 5);
  return (
    <div className={`mb-4 rounded-lg border px-4 py-3 text-xs ${tieout.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="spec-tieout">
      <div className="flex items-center gap-2 font-semibold">
        {tieout.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
        <span data-testid="spec-tieout-summary">Tie-out: {tieout.checked} figures checked · {tieout.ok ? "every one is in the estimate" : `${issues.length} finding${issues.length === 1 ? "" : "s"} block the PDF`}</span>
      </div>
      {!tieout.ok && (
        <>
          <p className="mt-1 text-amber-800/90">Every number on the page has to exist in the estimate before the PDF can go out. Each finding below shows the figure, where it sits, and the full sentence so you can see what to change.</p>
          <ol className="mt-3 space-y-3" data-testid="spec-tieout-list">
            {shown.map((m, i) => (
              <li key={i} className="rounded-md bg-white/70 border border-amber-200 px-3 py-2" data-testid={`spec-tieout-finding-${i}`}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-mono text-sm font-bold text-amber-950">{m.raw}</span>
                  <span className="text-amber-800">in <b>{m.section}</b></span>
                </div>
                <div className="mt-1 text-[13px] leading-relaxed text-slate-800 whitespace-normal break-words" data-testid={`spec-tieout-sentence-${i}`}>
                  “<Highlight text={m.context} raw={m.raw} />”
                </div>
                {m.why && <div className="mt-1 text-amber-900"><span className="font-semibold">Why: </span>{m.why}</div>}
                {m.fix && <div className="mt-0.5 text-slate-700"><span className="font-semibold">Fix: </span>{m.fix}</div>}
              </li>
            ))}
          </ol>
          {issues.length > 5 && (
            <button onClick={() => setShowAll((v) => !v)} className="mt-2 text-[#003E87] hover:underline font-medium" data-testid="spec-tieout-toggle">
              {showAll ? "Show fewer" : `Show all ${issues.length} findings`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
